'use client'

import { Sheet } from 'fukku/Sheet'
import { IconDownSmall } from 'icons/components/IconDownSmall'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { SizesBottomSheet } from 'product-components/components/SizeSelector/SizesBottomSheet/SizesBottomSheet'
import { cx } from 'utils/cx'

import { dataTestIds } from '../../../constants/dataTestIds/dataTestIds'
import { useAnalytics } from '../../../contexts/AnalyticsProvider/AnalyticsProvider'
import { useColors } from '../../../contexts/ColorsProvider/ColorsProvider'
import { useSizeGuide } from '../../../contexts/SizeGuideProvider/SizeGuideProvider'
import { useSizes } from '../../../contexts/SizesProvider/SizesProvider'
import { getShowSizeGuideByDisclaimer } from '../../../utils/getShowSizeGuideByDisclaimer/getShowSizeGuideByDisclaimer'
import { SizeGuide } from '../../SizeGuide/SizeGuide'
import { SizeDisclaimer } from '../SizeDisclaimer/SizeDisclaimer'
import { SizesList } from '../SizesList/SizesList'

import { useAbTest } from 'ab-test/hooks/useAbTest/useAbTest'
import text from 'fukku/styles/texts.module.scss'
import type { FullProduct } from 'pdp/src/types/FullProduct'
import type { SelectedSize } from 'product-components/components/SizeSelector/SizesList/SizesList'
import type { Product } from 'product/types'
import type { Stock } from 'product/types/Stock'
import { AbTestKeys } from 'types/constants/AbTestKeys'
import { useDelayedDays } from '../../../contexts/DelayedDaysProvider/DelayedDaysProvider'
import { useSizeItemClick } from '../SizeItem/useSizeItemClick/useSizeItemClick'
import { useReduceProductStock } from '../useReduceProductStock/useReduceProductStock'
import styles from './SizesSheet.module.scss'

type SizesSheetProps = {
	readonly className?: string
	readonly product: FullProduct
}

export function SizesSheet({
	className,
	product,
}: SizesSheetProps): React.ReactNode {
	const { t } = useLabels()
	const { sendClickSizeGuide, sendShowSizeSelectorEvent } = useAnalytics()
	const abTest = useAbTest({ key: AbTestKeys.PdpSizesSheet })

	const { selectedColor } = useColors()
	const { hasSizeGuide, setIsSizeGuideOpen } = useSizeGuide()
	const productStock: Stock = useReduceProductStock({ product })
	const {
		selectedSize,
		isUniqueSize,
		isSheetOpen,
		openSheet,
		hasRelatedSizes,
		sizeSelectorRef,
	} = useSizes()
	const withSheet = !isUniqueSize

	const onSelectedSize = useSizeItemClick({ withSheet, isUnselectable: false })
	const { hasDelayedWareHouse, deliveryTimeMessage } = useDelayedDays()

	const shouldShowSizeGuide =
		hasSizeGuide && getShowSizeGuideByDisclaimer(selectedColor.disclaimer)

	function onSelectSizeLabelClick(): void {
		openSheet(true)
		sendShowSizeSelectorEvent()
	}

	function onClose(): void {
		openSheet(false)
	}

	function onSizeGuideLink(): void {
		sizeSelectorRef?.current?.close()
		setIsSizeGuideOpen(true)
		sendClickSizeGuide()
	}

	return (
		<>
			{hasRelatedSizes && (
				<button
					type='button'
					className={cx(styles.sizesSheetButton, styles.selectable, className)}
					onClick={onSelectSizeLabelClick}
					data-testid={dataTestIds['pdp.productInfo.sizeSelector']}
				>
					<span className={text.bodyMRegular}>
						{selectedSize
							? selectedSize.label
							: t('pdp.modal.sizeSelector.title')}
					</span>
					<IconDownSmall />
				</button>
			)}

			{withSheet && abTest?.enabled && (
				<SizesBottomSheet
					product={product as unknown as Product}
					productId={product.id}
					selectedColorId={selectedColor.id}
					ref={sizeSelectorRef}
					onSelectedSize={(sizeSelected: SelectedSize) =>
						onSelectedSize(sizeSelected.size)
					}
					productStock={productStock}
					hasSizesGuide={shouldShowSizeGuide}
					onSizeGuideLink={onSizeGuideLink}
					delayedMessage={deliveryTimeMessage}
					delayedWareHouse={hasDelayedWareHouse}
					data-testid={dataTestIds['pdp.sizes.sheet']}
				/>
			)}

			{withSheet && !abTest?.enabled && (
				<Sheet
					name='pdp.sheetSizeSelector'
					header={
						<div className={styles.sizesSheetHeader}>
							<p className={cx(text.titleM)}>
								{t('pdp.modal.sizeSelector.title')}
							</p>
							<SizeDisclaimer
								disclaimer={selectedColor.disclaimer}
								className={styles.sizeDisclaimer}
							/>
						</div>
					}
					body={
						<SizesList
							sizes={selectedColor.sizes}
							selectedSize={selectedSize}
							withSheet
						/>
					}
					open={isSheetOpen}
					onClose={onClose}
					bubble={
						shouldShowSizeGuide && (
							<SizeGuide isSheet className={styles.sizeGuideSheet} />
						)
					}
				/>
			)}
		</>
	)
}
